import { useState } from 'react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import GoogleLogin, { GoogleUser } from './GoogleLogin'

// 统一的Agent12 Logo组件
const Agent12Logo: React.FC<{ size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'; className?: string }> = ({ 
  size = 'md', 
  className = '' 
}) => {
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4', 
    md: 'w-6 h-6',
    lg: 'w-10 h-10',
    xl: 'w-20 h-20'
  }
  
  const textSizes = {
    xs: 'text-xs',
    sm: 'text-xs',
    md: 'text-xs',
    lg: 'text-sm',
    xl: 'text-lg'
  }

  return (
    <div className={`${sizeClasses[size]} bg-gradient-to-r from-blue-600 to-purple-600 rounded-md flex items-center justify-center ${className}`}>
      <span className={`text-white font-bold ${textSizes[size]}`}>A12</span>
    </div>
  )
}

interface LoginPageProps {
  onLogin: (user: GoogleUser) => void
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleGoogleSuccess = (user: GoogleUser) => {
    console.log('LoginPage: 收到Google登录成功回调', user)
    setIsLoading(true)
    setError(null)
    
    try {
      console.log('LoginPage: 准备调用父组件的onLogin')
      // 直接调用父组件的登录回调，让父组件处理状态保存
      onLogin(user)
      console.log('LoginPage: 已调用父组件的onLogin')
      
      // 给父组件一点时间来更新状态，然后隐藏加载状态
      setTimeout(() => {
        setIsLoading(false)
      }, 100)
      
    } catch (err) {
      console.error('LoginPage: 登录处理错误:', err)
      setError('登录处理失败，请重试')
      setIsLoading(false)
    }
  }

  const handleGoogleError = (error: any) => {
    console.error('LoginPage: Google登录错误:', error)
    setError('Google登录失败，请重试')
    setIsLoading(false)
  }

  // 临时测试登录函数
  const handleTestLogin = () => {
    console.log('LoginPage: 使用测试账号登录')
    const testUser: GoogleUser = {
      id: 'test-user-' + Date.now(),
      name: '测试用户',
      email: 'test@example.com',
      picture: '',
      given_name: '测试',
      family_name: '用户'
    }
    handleGoogleSuccess(testUser)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        {/* 头部 */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-6 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Agent12Logo size="md" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">agent12</h1>
          <p className="text-blue-100">智能对话助手</p>
        </div>

        {/* 主要内容 */}
        <div className="p-8">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">欢迎使用</h2>
            <p className="text-gray-600">请使用Google账号登录以开始使用</p>
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* 加载状态 */}
          {isLoading && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-sm text-blue-700">登录中，请稍候...</span>
            </div>
          )}

          {/* Google 登录 */}
          <div className="space-y-4">
            <GoogleLogin 
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
            />
            
            {/* 分隔线 */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">或者</span>
              </div>
            </div>
            
            {/* 测试登录按钮 */}
            <button
              onClick={handleTestLogin}
              disabled={isLoading}
              className="w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors border border-gray-300 disabled:opacity-50"
            >
              {isLoading ? '登录中...' : '使用测试账号登录'}
            </button>
          </div>

          {/* 功能介绍 */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <div className="grid grid-cols-1 gap-3 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>智能对话与问答</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span>多模态内容处理</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>个性化体验</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage 